// « Aucune règle surveillée sur ce compte » — rendre visible un silence.
//
// POURQUOI (mesuré en prod, 2026-09-26)
// 23 comptes prop firm ACTIFS (18 challenge, 5 pa) n'ont aucun template, donc
// aucun palier résolu, donc `bufferStatus.ruleType` à null : ni objectif, ni
// plancher, ni perte journalière surveillés. Aucun breach ne sera JAMAIS
// détecté sur ces comptes, et jusqu'ici rien ne le disait à leur propriétaire.
// Une jauge vide se lit « tout va bien », pas « je ne mesure rien ».
//
// Le garde de `confirmTradovateImport` empêche d'en créer de nouveaux. Cette
// bannière traite ceux qui EXISTENT : on ne réécrit pas leurs données, on rend
// l'angle mort visible et on donne le geste qui le comble.
//
// Volontairement une BANNIÈRE et non un modal, et sans action destructrice :
// même parti que RuleUpdateBanner. Le compte reste utilisable, son journal est
// intact ; seule la surveillance manque.

import { Link } from "react-router-dom";
import { Button } from "../../client/components/ui/button";
import useTranslation from "../../client/hooks/useTranslation";
import { ShieldOff, ArrowRight } from "lucide-react";

export interface NoRulesAccount {
  id: string;
  name: string;
  /** Posé par buildEnrichedAccounts (voir propFirmRequirement.ts). */
  hasNoRules?: boolean;
}

/**
 * @param accounts  Comptes enrichis à examiner. La bannière se filtre elle-même
 *                  sur `hasNoRules` : les appelants passent leur liste telle
 *                  quelle, sans dupliquer la règle.
 * @param onConfigure  Ouvre l'édition du compte. Omis, la bannière affiche un
 *                     lien vers la fiche compte à la place.
 */
export default function NoRulesBanner({
  accounts,
  onConfigure,
}: {
  accounts: NoRulesAccount[];
  onConfigure?: (accountId: string) => void;
}) {
  const t = useTranslation();

  const affected = accounts.filter((a) => a.hasNoRules === true);
  if (affected.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
      <div className="flex items-start gap-3">
        <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{t.tradingAccounts.noRulesTitle}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t.tradingAccounts.noRulesBody}</p>

          <ul className="mt-3 flex flex-wrap gap-2">
            {affected.map((a) => (
              <li key={a.id}>
                {onConfigure ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => onConfigure(a.id)}
                  >
                    <span className="max-w-[16rem] truncate">{a.name}</span>
                    <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                  </Button>
                ) : (
                  <Button asChild variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
                    <Link to={`/account/${a.id}`}>
                      <span className="max-w-[16rem] truncate">{a.name}</span>
                      <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                    </Link>
                  </Button>
                )}
              </li>
            ))}
          </ul>

          <p className="mt-2 text-[11px] text-muted-foreground">
            {t.tradingAccounts.noRulesAction}
          </p>
        </div>
      </div>
    </div>
  );
}
